import { useMemo } from "react";
import { rangeForPreset, RANGE_PRESET_LABELS } from "../lib/dashboardMetrics";
import { useTypedNavigate, useTypedSearch } from "../lib/typedRouter";
import {
  canonicalWorkingScopeSearch,
  useWorkingScope,
  useResolvedWorkingTeamId,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { FilterBar, InlineError, Page, PageHeader, Text } from "../ui";
import { ReviewListTable } from "./review/ReviewListTable";
import {
  parseReviewListSearch,
  type ReviewListSearch,
} from "./review/reviewListData";
import { useReviewListData } from "./review/useReviewListData";

export function ReviewList() {
  const { agentId } = useWorkingScope();
  const teamId = useResolvedWorkingTeamId();
  const search = useTypedSearch(parseReviewListSearch);
  const navigate = useTypedNavigate<ReviewListSearch & WorkingScopeSearch>();
  // ASSUMED(Review defaults to the existing Alignment 30-day window.)
  const range = search.range ?? "30d";
  const timeWindow = useMemo(() => rangeForPreset(range), [range]);
  const enabled = Boolean(agentId || teamId);
  const data = useReviewListData(
    {
      time_range: timeWindow,
      agent_id: agentId ?? null,
      team_id: agentId ? null : (teamId ?? null),
      agent_version_id: search.version ?? null,
      batch_id: null,
      source: null,
    },
    enabled,
  );
  return (
    <Page variant="workspace">
      <PageHeader
        compact
        leading={
          <FilterBar
            facets={[
              {
                id: "range",
                kind: "single",
                label: "Time range",
                removable: false,
                options: (["7d", "30d", "90d"] as const).map((value) => ({
                  value,
                  label: RANGE_PRESET_LABELS[value],
                })),
              },
              {
                id: "version",
                kind: "single",
                label: "Agent version",
                options: data.versions.items.map((version) => ({
                  label: version.tag,
                  value: version.id,
                })),
              },
            ]}
            value={{
              range: [range],
              version: search.version ? [search.version] : [],
            }}
            onChange={(value) => {
              void navigate({
                replace: true,
                search: (previous) => ({
                  ...canonicalWorkingScopeSearch(previous),
                  ...parseReviewListSearch({
                    range: Array.isArray(value.range) ? value.range[0] : range,
                    version: Array.isArray(value.version)
                      ? value.version[0]
                      : undefined,
                  }),
                }),
              });
            }}
          />
        }
      />
      {data.versions.errorMessage ? (
        <InlineError>{data.versions.errorMessage}</InlineError>
      ) : null}
      {data.notice ? <Text variant="muted">{data.notice}</Text> : null}
      <ReviewListTable
        rows={data.rows}
        isPending={enabled && data.isPending}
        error={data.error}
        onOpenMarkers={() => {
          void navigate({
            to: "/markers",
            search: canonicalWorkingScopeSearch,
          });
        }}
        onOpen={(markerId) => {
          void navigate({
            to: "/review/$markerId",
            params: { markerId },
            search: (previous) => ({
              ...canonicalWorkingScopeSearch(previous),
              ...search,
            }),
          });
        }}
      />
    </Page>
  );
}
